import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, Upload, Download, FileText, Trash2, QrCode, CheckCircle2, Circle, Eye, UserMinus, RotateCcw } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const DOC_CHECKLIST = [
  '10th Marksheet', '12th Marksheet', 'Graduation (DMC)', 'PAN Card', 'Aadhar Card',
  'Experience Letter', 'Photo', 'PF / ESI Number', 'Relieving Letter', 'Offer Letter',
  'Salary Slip (Last 3 Months)', 'Bank Details',
];
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { employeeApi, salaryApi, kpiApi, documentApi, masterApi, resolveMediaUrl } from '../lib/api';
import { SourceOfHiringFields, ExperienceFields, HiringSummary, useHiringMeta } from '../components/HiringFields';
import { fullAgeFrom, tenureFrom } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import SuggestionsTab from './SuggestionsTab';
import KpiTab from './KpiTab';
import KaizenTab from './KaizenTab';
import LettersTab from './LettersTab';
import InductionTab from './InductionTab';
import SalaryBreakupYears from './SalaryBreakupTab';
import DocumentScanner from '../components/DocumentScanner';
import EducationSection from '../components/EducationSection';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import ResignDialog from '../components/ResignDialog';

export default function EmployeeProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [kpiData, setKpiData] = useState([]);
  const [kpiScoreEdit, setKpiScoreEdit] = useState({});
  const [kpiToken, setKpiToken] = useState('');
  const [kpiTokenStatus, setKpiTokenStatus] = useState(null); // { has_link, expires }
  const [kpiLinkOpen, setKpiLinkOpen] = useState(false);
  const [scanFor, setScanFor] = useState(null);  // requirement name currently being scanned
  const [viewDoc, setViewDoc] = useState(null);  // {id, doc_type, filename} of document being previewed
  const [newYear, setNewYear] = useState('');
  const [newGross, setNewGross] = useState('');
  // Pre-fill the add-year form with what the system already knows: joining year + joining salary.
  useEffect(() => {
    if (!employee) return;
    const jy = employee.year_join || (employee.doj ? (String(employee.doj).includes('.') ? String(employee.doj).split('.')[2] : new Date(employee.doj).getFullYear()) : '');
    setNewYear((v) => v || (jy ? String(jy) : ''));
    setNewGross((v) => v || (employee.joining_salary != null ? String(employee.joining_salary) : ''));
    // eslint-disable-next-line
  }, [employee?.id, employee?.doj, employee?.joining_salary]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [managerOptions, setManagerOptions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [resignOpen, setResignOpen] = useState(false);
  const [corp, setCorp] = useState(false);
  const [masters, setMasters] = useState({ units: [], departments: [], designations: [] });
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [pendingIncrement, setPendingIncrement] = useState(null);
  const [promoEffective, setPromoEffective] = useState(new Date().toISOString().slice(0, 10));
  const [letterDate, setLetterDate] = useState(new Date().toISOString().slice(0, 10));
  const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';

  useEffect(() => {
    Promise.all([masterApi.units(), masterApi.departments(), masterApi.designations()])
      .then(([u, d, g]) => setMasters({ units: u.data, departments: d.data, designations: g.data })).catch(() => {});
  }, []);

  useEffect(() => {
    loadEmployee();
  }, [id]);

  useEffect(() => {
    if (!employee?.id) return;
    const params = {};
    if (employee?.level) params.for_level = employee.level;
    if (employee?.unit) params.unit = employee.unit;
    employeeApi.managers(params).then((res) => {
      setManagerOptions(res.data.filter((m) => m.id !== employee?.id));
    }).catch(() => {});
  }, [employee?.id, employee?.level, employee?.unit]);

  useEffect(() => { setCorp(!!employee?.is_corporate); }, [employee?.is_corporate]);

  // Deep link into a tab, e.g. the confirmation-due prompt sends HR straight
  // to ?tab=letters. Ignored unless the tab is one this user can actually see.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (!t) return;
    // Any tab, not just letters: the KPI Center links here with ?tab=kpi because
    // this sheet is the single place a score is entered, and a link that landed
    // on the wrong tab would send people looking for it elsewhere.
    const allowed = {
      profile: true, kpi: hasPerm('kpi.view'), suggestions: true, kaizen: true,
      salary: hasPerm('salary.view'), letters: hasPerm('letters.view'), documents: true,
      induction: true,
    };
    if (allowed[t]) setActiveTab(t);
    // eslint-disable-next-line
  }, [searchParams]);

  // The Edit action in the Employees list links straight here with ?edit=1,
  // so Edit is one click from the row rather than two.
  useEffect(() => {
    if (searchParams.get('edit') === '1' && hasPerm('employee.edit')) setEditOpen(true);
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    if (!employee?.id || !canIssueLetters) return;
    const kind = searchParams.get('letter');
    if (kind === 'promotion') {
      setPromoEffective(new Date().toISOString().slice(0, 10));
      setPendingPromotion({ from: searchParams.get('from') || employee.designation, to: searchParams.get('to') || employee.designation });
    } else if (kind === 'transfer') {
      const fu = searchParams.get('fu'), tu = searchParams.get('tu'), fd = searchParams.get('fd'), td = searchParams.get('td');
      setPromoEffective(new Date().toISOString().slice(0, 10));
      setPendingTransfer({ unitChanged: !!(tu && tu !== fu), deptChanged: !!(td && td !== fd), fromUnit: fu, toUnit: tu, fromDept: fd, toDept: td });
    }
    // eslint-disable-next-line
  }, [employee?.id, searchParams]);

  const loadEmployee = async () => {
    try {
      const empRes = await employeeApi.get(id);
      setEmployee(empRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load employee');
      setLoading(false);
    }
  };

  const canIssueLetters = hasPerm('letters.generate');

  const openPromotionLetter = async () => {
    if (!pendingPromotion) return;
    try {
      const res = await employeeApi.promotionLetter(employee.id, {
        from_designation: pendingPromotion.from,
        to_designation: pendingPromotion.to,
        effective_date: promoEffective,
        letter_date: letterDate,
        logo_url: LOGO_URL,
      });
      const w = window.open('', '_blank');
      if (!w) { toast.error('Allow pop-ups to open the letter'); return; }
      w.document.open();
      w.document.write(res.data.html);
      w.document.close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate letter');
    }
  };

  const openIncrementLetter = async () => {
    if (!pendingIncrement) return;
    try {
      const res = await employeeApi.incrementLetter(employee.id, {
        prev_gross: pendingIncrement.from,
        new_gross: pendingIncrement.to,
        effective_date: promoEffective,
        letter_date: letterDate,
        logo_url: LOGO_URL,
      });
      const w = window.open('', '_blank');
      if (!w) { toast.error('Allow pop-ups to open the letter'); return; }
      w.document.open();
      w.document.write(res.data.html);
      w.document.close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate letter');
    }
  };

  const openIdCard = () => {
    const logo = window.location.origin + '/assets/aampl-logo.jpg';
    const yoj = employee?.doj ? (String(employee.doj).includes('.') ? String(employee.doj).split('.')[2] : new Date(employee.doj).getFullYear()) : '';
    const initials = (employee?.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
    const photo = employee?.photo_url
      ? `<img src="${resolveMediaUrl(employee.photo_url)}" style="width:96px;height:96px;border-radius:50%;object-fit:cover;border:3px solid #fff;" />`
      : `<div style="width:96px;height:96px;border-radius:50%;background:#EFF1F6;display:flex;align-items:center;justify-content:center;font-size:32px;font-weight:700;color:#98A1B2;border:3px solid #fff;">${initials}</div>`;
    const row = (label, val) => `<div style="display:flex;justify-content:space-between;gap:8px;padding:5px 0;border-bottom:1px solid #EEF1F6;"><span style="color:#98A1B2;font-size:10px;text-transform:uppercase;letter-spacing:.04em;">${label}</span><span style="color:#11182B;font-weight:600;font-size:12px;text-align:right;">${val || '—'}</span></div>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to open the ID card'); return; }
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>ID Card — ${employee?.name || ''}</title>
    <style>
      @page { size: 320px 470px; margin: 0; }
      * { box-sizing: border-box; }
      body { margin:0; font-family:'Inter',Arial,sans-serif; display:flex; align-items:center; justify-content:center; background:#F2F4F8; }
      .card { width:320px; height:470px; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 8px 30px rgba(16,36,104,.18); }
      .head { background:linear-gradient(135deg,#0C1B57,#2563EB); color:#fff; padding:16px; text-align:center; }
      .head img { height:34px; object-fit:contain; background:#fff; border-radius:6px; padding:3px; }
      .head h2 { margin:8px 0 0; font-size:14px; letter-spacing:1px; }
      .photo { margin:-40px auto 8px; width:96px; }
      .body { padding:0 20px 16px; text-align:center; }
      .name { font-size:18px; font-weight:700; color:#11182B; margin:6px 0 2px; }
      .role { font-size:12px; color:#2563EB; font-weight:600; }
      .dept { font-size:10px; color:#98A1B2; text-transform:uppercase; letter-spacing:.06em; margin-bottom:10px; }
      .rows { text-align:left; margin-top:8px; }
      .btn { position:fixed; top:12px; left:12px; font-family:Arial; background:#2563EB; color:#fff; border:0; padding:8px 14px; border-radius:6px; cursor:pointer; }
      @media print { .btn { display:none; } body { background:#fff; } }
    </style></head><body>
      <button class="btn" onclick="window.print()">Print / Save as PDF</button>
      <div class="card">
        <div class="head"><img src="${logo}" onerror="this.style.display='none'" alt="AAMPL"/><h2>AAMPL</h2></div>
        <div class="photo">${photo}</div>
        <div class="body">
          <div class="name">${employee?.name || ''}</div>
          <div class="role">${employee?.designation || ''}${employee?.level ? ` · L${employee.level}` : ''}</div>
          <div class="dept">${employee?.department || ''}</div>
          <div class="rows">
            ${row('Card No', employee?.card_no)}
            ${row('Unit', employee?.unit)}
            ${row('Department', employee?.department)}
            ${row('Year of Joining', yoj)}
          </div>
        </div>
      </div>
    </body></html>`);
    w.document.close();
  };

  const openTransferLetter = async () => {
    if (!pendingTransfer) return;
    try {
      const res = await employeeApi.transferLetter(employee.id, {
        from_unit: pendingTransfer.fromUnit, to_unit: pendingTransfer.toUnit,
        from_department: pendingTransfer.fromDept, to_department: pendingTransfer.toDept,
        effective_date: promoEffective,
        letter_date: letterDate,
        logo_url: LOGO_URL,
      });
      const w = window.open('', '_blank');
      if (!w) { toast.error('Allow pop-ups to open the letter'); return; }
      w.document.open();
      w.document.write(res.data.html);
      w.document.close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate letter');
    }
  };

  const addSalaryYear = async () => {
    const y = parseInt(newYear, 10), g = parseFloat(newGross);
    if (!y || isNaN(g)) { toast.error('Enter a valid year and gross'); return; }
    try { const res = await salaryApi.backfill(id, { year: y, gross: g }); setSalaryHistory(res.data); setNewYear(''); setNewGross(''); toast.success(`${y} salary saved`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };
  const deleteSalaryYear = async (year) => {
    if (!window.confirm(`Remove the ${year} salary row?`)) return;
    try { await salaryApi.deleteYear(id, year); const r = await salaryApi.get(id); setSalaryHistory(r.data); toast.success('Removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const loadSalary = async () => {
    try {
      const res = await salaryApi.get(id);
      setSalaryHistory(res.data);
    } catch (error) {
      console.error('Salary access denied or failed:', error);
      setSalaryHistory([]);
    }
  };

  const verifyKpi = async (row) => {
    const key = `${row.fy}-${row.month}`;
    try {
      await kpiApi.update(id, {
        fy: row.fy, month: row.month, suggestion: row.suggestion, kaizen: row.kaizen,
        poka_yoke: row.poka_yoke, saving: row.saving, ncs: row.ncs,
        kpi_score: Number(kpiScoreEdit[key] ?? row.kpi_score) || 0,
      });
      toast.success('KPI verified');
      const res = await kpiApi.get(id);
      setKpiData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Verify failed'); }
  };

  const openNoDues = async () => {
    try {
      const LOGO = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';
      const r = await employeeApi.noDues(employee.id, { logo_url: LOGO, date_of_leaving: employee.left_at });
      const w = window.open('', '_blank');
      if (w) { w.document.write(r.data.html); w.document.close(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate No Dues form'); }
  };

  const openKpiLink = async () => {
    try {
      // One permanent token per employee — the same value every time, so the
      // QR can always be re-shown and never needs regenerating.
      const r = await employeeApi.kpiLink(employee.id);
      setKpiToken(r.data?.token || '');
      setKpiTokenStatus({ has_link: !!r.data?.has_link, expires: null });
      setKpiLinkOpen(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not get the QR link');
    }
  };

  const loadKPI = async () => {
    try {
      const res = await kpiApi.get(id);
      setKpiData(res.data);
    } catch (error) {
      toast.error('Failed to load KPI data');
    }
  };

  const canUploadDocs = hasPerm('appraisal.upload');
  const uploadDoc = async (docType, file) => {
    if (!file) return;
    try { await documentApi.upload(id, docType, file); const r = await documentApi.list(id); setDocuments(r.data); toast.success(`${docType} uploaded`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };
  const removeDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await documentApi.remove(docId); const r = await documentApi.list(id); setDocuments(r.data); toast.success('Deleted'); }
    catch (e) { toast.error('Failed'); }
  };

  const loadDocuments = async () => {
    try {
      const res = await documentApi.list(id);
      setDocuments(res.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  useEffect(() => {
    if (activeTab === 'salary' && salaryHistory.length === 0) {
      loadSalary();
    } else if (activeTab === 'documents' && documents.length === 0) {
      loadDocuments();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex h-screen app-shell">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return <div>Employee not found</div>;
  }

  const canViewSalary = !employee._salary_hidden;
  const canEditEmployee = hasPerm('employee.edit');
  const canDeleteEmployee = hasPerm('employee.delete');
  const canEditSalary = hasPerm('salary.edit');

  return (
    <div data-testid="employee-profile-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/employees')}
              variant="ghost"
              className="mb-4 text-[#5A6377] hover:text-[#11182B]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Employees
            </Button>

            {pendingPromotion && (
              <div data-testid="promotion-banner" className="bg-[#FBF7EC] border border-[#E9A23B] rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#11182B]">
                  <span className="font-semibold">Promotion:</span> {pendingPromotion.from} → {pendingPromotion.to}
                  <label className="ml-4 text-[#5A6377]">Effective (w.e.f):&nbsp;
                    <input type="date" value={promoEffective} onChange={(e) => setPromoEffective(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                  <label className="ml-4 text-[#5A6377]">Printed on:&nbsp;
                    <input type="date" value={letterDate} onChange={(e) => setLetterDate(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid="generate-promotion-letter" onClick={openPromotionLetter}>
                    Generate Promotion Letter
                  </Button>
                  <button className="text-sm text-[#5A6377] hover:underline" onClick={() => setPendingPromotion(null)}>Dismiss</button>
                </div>
              </div>
            )}

            {pendingIncrement && (
              <div data-testid="increment-banner" className="bg-[#EAF7EE] border border-[#15A36E] rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#11182B]">
                  <span className="font-semibold">Salary revision:</span>{' '}
                  {pendingIncrement.from != null ? `₹${Number(pendingIncrement.from).toLocaleString('en-IN')}` : '—'} → {pendingIncrement.to != null ? `₹${Number(pendingIncrement.to).toLocaleString('en-IN')}` : '—'}
                  <label className="ml-4 text-[#5A6377]">Effective (w.e.f):&nbsp;
                    <input type="date" value={promoEffective} onChange={(e) => setPromoEffective(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                  <label className="ml-4 text-[#5A6377]">Printed on:&nbsp;
                    <input type="date" value={letterDate} onChange={(e) => setLetterDate(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid="generate-increment-letter" onClick={openIncrementLetter}>
                    Generate Increment Letter
                  </Button>
                  <button className="text-sm text-[#5A6377] hover:underline" onClick={() => setPendingIncrement(null)}>Dismiss</button>
                </div>
              </div>
            )}

            {pendingTransfer && (
              <div data-testid="transfer-banner" className="bg-[#EAF4FE] border border-[#2563EB] rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#11182B]">
                  <span className="font-semibold">{pendingTransfer.unitChanged ? 'Transfer' : 'Department change'}:</span>{' '}
                  {pendingTransfer.unitChanged && <>Unit {pendingTransfer.fromUnit} → {pendingTransfer.toUnit}</>}
                  {pendingTransfer.unitChanged && pendingTransfer.deptChanged && ' · '}
                  {pendingTransfer.deptChanged && <>Dept {pendingTransfer.fromDept} → {pendingTransfer.toDept}</>}
                  <label className="ml-4 text-[#5A6377]">Effective (w.e.f):&nbsp;
                    <input type="date" value={promoEffective} onChange={(e) => setPromoEffective(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                  <label className="ml-4 text-[#5A6377]">Printed on:&nbsp;
                    <input type="date" value={letterDate} onChange={(e) => setLetterDate(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1" />
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid="generate-transfer-letter" onClick={openTransferLetter}>
                    Generate {pendingTransfer.unitChanged ? 'Transfer' : 'Reassignment'} Letter
                  </Button>
                  <button className="text-sm text-[#5A6377] hover:underline" onClick={() => setPendingTransfer(null)}>Dismiss</button>
                </div>
              </div>
            )}

            {/* Serving notice. Still an employee, still on the chart, still
                scored — but everybody who opens this page sees when they go,
                and nobody has to move them on the day. */}
            {employee.on_notice && (
              <div data-testid="notice-period-banner"
                   className="mb-6 rounded-xl border border-[#F0D9AE] bg-[#FFFAF0] p-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-[#FBEAD3] text-[#8A5A12]">
                  Resignation / Notice Period
                </span>
                <span className="text-sm text-[#8A5A12]">
                  <b>{employee.name}</b> resigned on {employee.resignation_date || '—'} and works until{' '}
                  <b>{employee.last_working_day}</b>
                  {employee.notice_days_left != null && employee.notice_days_left >= 0
                    ? ` — ${employee.notice_days_left} day${employee.notice_days_left === 1 ? '' : 's'} left`
                    : ''}.
                </span>
                <span className="text-[11px] text-[#98A1B2]">
                  Treated as an active employee until then, and moved to Ex-Employees automatically on the day.
                </span>
              </div>
            )}

            <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-8 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-20 h-20 rounded-full bg-[#EFF1F6] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {employee?.photo_url
                      ? <img src={resolveMediaUrl(employee.photo_url)} alt={employee?.name} className="w-20 h-20 object-cover" />
                      : <span className="text-2xl font-bold text-[#98A1B2]">{(employee?.name || '?').split(' ').map(s => s[0]).slice(0,2).join('')}</span>}
                  </div>
                  <div>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554] mb-2">{employee.name}</h1>
                    <div className="flex gap-4 text-sm text-[#5A6377]">
                      <span>{employee.designation}</span>
                      <span>•</span>
                      <span>{employee.department}</span>
                      <span>•</span>
                      <span>{employee.unit}</span>
                    </div>
                    {hasPerm('employee.edit') && (
                      <div className="flex items-center gap-3 mt-2">
                        <label data-testid="upload-photo-label" className="text-xs font-medium text-[#2563EB] cursor-pointer hover:underline">
                          Upload photo
                          <input
                            data-testid="upload-photo-input"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              try {
                                await employeeApi.uploadPhoto(employee.id, f);
                                toast.success('Photo updated');
                                await loadEmployee();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Upload failed');
                              }
                            }}
                          />
                        </label>
                        {employee?.photo_url && (
                          <button
                            data-testid="remove-photo-button"
                            className="text-xs text-[#E11D2A] hover:underline"
                            onClick={async () => {
                              try {
                                await employeeApi.removePhoto(employee.id);
                                toast.success('Photo removed');
                                await loadEmployee();
                              } catch (err) {
                                toast.error('Failed');
                              }
                            }}
                          >Remove</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {canEditEmployee && (
                  <div className="flex items-center gap-2">
                    <Button data-testid="edit-employee-button" variant="outline" className="border-[#E6E9F0]" onClick={() => setEditOpen(true)}>
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    {/* Resign sits beside Edit, never inside it. */}
                    {employee.status !== 'Left' && !employee.on_notice && (
                      <Button data-testid="profile-resign-button" variant="outline"
                              className="border-[#F5C9CC] text-[#E11D2A] hover:bg-[#FDE7E9]"
                              onClick={() => setResignOpen(true)}>
                        <UserMinus className="w-4 h-4 mr-2" /> Resign
                      </Button>
                    )}
                    {(employee.on_notice || employee.status === 'Left') && (
                      <Button data-testid="cancel-resignation-button" variant="outline"
                              className="border-[#E6E9F0] text-[#15A36E]"
                              onClick={async () => {
                                if (!window.confirm(
                                  `Withdraw the resignation for ${employee.name}? Their exit dates and reason are cleared, and the vacancy raised for their seat is closed.`)) return;
                                try {
                                  await employeeApi.cancelResignation(employee.id);
                                  toast.success('Resignation withdrawn');
                                  await loadEmployee();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Could not withdraw the resignation');
                                }
                              }}>
                        <RotateCcw className="w-4 h-4 mr-2" /> Withdraw resignation
                      </Button>
                    )}
                    {canDeleteEmployee && (
                      <Button
                        data-testid="delete-employee-button"
                        variant="outline"
                        className="border-[#F5C9CC] text-[#E11D2A] hover:bg-[#FDE7E9]"
                        onClick={async () => {
                          if (!window.confirm(`Move ${employee.name} to the Recycle Bin? You can restore them later. For a real departure use the Resign action instead — it keeps the record and raises the vacancy.`)) return;
                          try {
                            await employeeApi.trash(employee.id);
                            toast.success('Moved to Recycle Bin');
                            navigate('/employees');
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Delete failed');
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Recycle Bin
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-[#E6E9F0]">
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Card Number</p>
                  <p className="text-sm text-[#11182B]">{employee.card_no}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Date of Joining</p>
                  <p className="text-sm text-[#11182B] flex items-center gap-2 flex-wrap">
                    {employee.doj}
                    {tenureFrom(employee.doj) && (
                      <span data-testid="employee-tenure" title="Time with the company"
                        className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E8EFFD] text-[#15307F] whitespace-nowrap">
                        {tenureFrom(employee.doj)}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Date of Birth</p>
                  <p data-testid="employee-dob" className="text-sm text-[#11182B] flex items-center gap-2 flex-wrap">
                    {employee.dob || <span className="text-[#98A1B2]">Not recorded</span>}
                    {fullAgeFrom(employee.dob) && (
                      <span data-testid="employee-age" title="Current age"
                        className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E7F7EF] text-[#15654B] whitespace-nowrap">
                        {fullAgeFrom(employee.dob)}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Email</p>
                  <p data-testid="employee-email" className="text-sm text-[#11182B] break-all">
                    {employee.email || <span className="text-[#98A1B2]">Not recorded</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Contact Number</p>
                  <p data-testid="employee-mobile" className="text-sm text-[#11182B]">
                    {employee.mobile || <span className="text-[#98A1B2]">Not recorded</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#5A6377] mb-1">Status</p>
                  <span data-testid="employee-status-badge" className={`px-2.5 py-0.5 rounded-full text-xs font-medium inline-block ${
                    employee.on_notice ? 'bg-[#FBEAD3] text-[#8A5A12]'
                      : employee.status === 'Active' ? 'bg-[#E4F6EE] text-[#15A36E]'
                      : 'bg-[#FDE7E9] text-[#E11D2A]'}`}>
                    {employee.on_notice ? 'On Notice Period'
                      : employee.status === 'Left' ? (employee.exit_reason || 'Resigned')
                      : employee.status}
                  </span>
                </div>
                {employee.resignation_date && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Resignation Date</p>
                    <p data-testid="employee-resignation-date" className="text-sm text-[#11182B]">{employee.resignation_date}</p>
                  </div>
                )}
                {(employee.status === 'Left' || employee.on_notice) && employee.last_working_day && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Last Working Day</p>
                    <p data-testid="employee-last-working-day" className="text-sm text-[#11182B]">{employee.last_working_day}</p>
                  </div>
                )}
                {(employee.status === 'Left' || employee.on_notice) && employee.exit_remarks && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Reason for Leaving</p>
                    <p data-testid="employee-exit-remarks" className="text-sm text-[#11182B]">{employee.exit_remarks}</p>
                  </div>
                )}
                {employee.father_husband && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Father/Husband</p>
                    <p className="text-sm text-[#11182B]">{employee.father_husband}</p>
                  </div>
                )}
                {employee.education && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Education</p>
                    <p className="text-sm text-[#11182B]">{employee.education}</p>
                  </div>
                )}
                {/* Shown even when empty, unlike the fields above it: a blank
                    source is the thing HR is looking for when they audit
                    where hires come from, so it has to be visible. */}
                <HiringSummary employee={employee} />
                {employee.category && (
                  <div>
                    <p className="text-xs font-semibold text-[#5A6377] mb-1">Category</p>
                    <p className="text-sm text-[#11182B]">{employee.category}</p>
                  </div>
                )}
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="flex border-b border-[#E6E9F0] gap-8 bg-transparent">
                <TabsTrigger
                  data-testid="profile-tab"
                  value="profile"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  Profile
                </TabsTrigger>
                {canViewSalary && (
                  <TabsTrigger
                    data-testid="salary-tab"
                    value="salary"
                    className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                  >
                    Salary History
                  </TabsTrigger>
                )}
                <TabsTrigger
                  data-testid="kpi-tab"
                  value="kpi"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  KPI Monitoring
                </TabsTrigger>
                <TabsTrigger
                  data-testid="suggestions-tab"
                  value="suggestions"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  Suggestions
                </TabsTrigger>
                <TabsTrigger
                  data-testid="kaizen-tab"
                  value="kaizen"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  Kaizen
                </TabsTrigger>
                {hasPerm('letters.view') && (
                  <TabsTrigger
                    data-testid="letters-tab"
                    value="letters"
                    className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                  >
                    Letters
                  </TabsTrigger>
                )}
                <TabsTrigger
                  data-testid="induction-tab-trigger"
                  value="induction"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  Induction
                </TabsTrigger>
                <TabsTrigger
                  data-testid="documents-tab"
                  value="documents"
                  className="pb-3 text-sm font-medium text-[#5A6377] hover:text-[#11182B] border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#11182B] transition-all"
                >
                  Documents
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-6">
                <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Employee Details</h2>
                    <div className="flex items-center gap-2">
                      {hasPerm('kpi.edit') && (
                        <Button data-testid="kpi-link-button" variant="outline" size="sm" className="border-[#E6E9F0]" onClick={() => openKpiLink()}>
                          <QrCode className="w-4 h-4 mr-2" /> KPI Form
                        </Button>
                      )}
                      <Button data-testid="download-id-card" variant="outline" size="sm" className="border-[#E6E9F0]" onClick={openIdCard}>
                        <Download className="w-4 h-4 mr-2" /> ID Card
                      </Button>
                      {employee?.status === 'Left' && hasPerm('letters.generate') && (
                        <Button data-testid="no-dues-button" variant="outline" size="sm" className="border-[#E6E9F0]" onClick={openNoDues}>
                          <FileText className="w-4 h-4 mr-2" /> No Dues Form
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Full Name</p>
                      <p className="text-sm text-[#11182B]">{employee.name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Card Number</p>
                      <p className="text-sm text-[#11182B]">{employee.card_no}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Unit</p>
                      <p className="text-sm text-[#11182B]">{employee.unit}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Department</p>
                      <p className="text-sm text-[#11182B]">{employee.department}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Designation</p>
                      <p className="text-sm text-[#11182B]">{employee.designation}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Year of Joining</p>
                      <p className="text-sm text-[#11182B]">{employee?.doj ? (String(employee.doj).includes('.') ? String(employee.doj).split('.')[2] : new Date(employee.doj).getFullYear()) : '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Date of Birth</p>
                      <p className="text-sm text-[#11182B]">{employee.dob || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#5A6377] mb-1">Contact Number</p>
                      <p className="text-sm text-[#11182B]">{employee.mobile || '—'}</p>
                    </div>
                    {canViewSalary && employee.joining_salary && (
                      <div>
                        <p className="text-xs font-semibold text-[#5A6377] mb-1">Joining Salary</p>
                        <p className="text-sm text-[#11182B]">₹{employee.joining_salary.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reporting & level */}
                <div className="bg-white border border-[#E6E9F0] rounded-xl p-6 mt-4">
                  <h3 className="text-sm font-semibold text-[#11182B] mb-3">Reporting & Level</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Level</label>
                      <div className="text-sm text-[#11182B]">{employee.level ? `L${employee.level}` : '—'} <span className="text-[#98A1B2]">({employee.designation})</span></div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#5A6377] mb-1.5 block">Reports To</label>
                      {employee.status === 'Left' ? (
                        <div className="text-sm text-[#98A1B2]">Former employee — not part of the reporting line</div>
                      ) : (
                        <select
                          data-testid="reporting-to-select"
                          className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5 w-full"
                          value={employee.reporting_to || ''}
                          onChange={async (e) => {
                            const val = e.target.value || null;
                            try {
                              await employeeApi.update(employee.id, { reporting_to: val });
                              toast.success('Reporting line updated');
                              await loadEmployee();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Failed to update reporting line');
                            }
                          }}
                        >
                        <option value="">— None (top of tree) —</option>
                        {/* An open position is a valid manager: the seat exists
                            in the org chart whether or not somebody is sitting
                            in it. Labelled VACANT so nobody picks one thinking
                            it is a person. */}
                        {managerOptions.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.level ? `L${m.level} · ` : ''}
                            {m.is_vacant
                              ? `${m.designation} — VACANT${m.department ? ` (${m.department})` : ''}`
                              : `${m.name} — ${m.designation}${m.is_corporate ? ' (Corporate)' : ''}`}
                          </option>
                        ))}
                      </select>
                      )}
                    </div>
                  </div>
                  {hasPerm('employee.transfer') && employee.status !== 'Left' && (
                    <div className="flex items-center gap-2 mt-4">
                      <Checkbox
                        data-testid="corporate-toggle"
                        checked={corp}
                        onCheckedChange={async (val) => {
                          const enabled = val === true;
                          setCorp(enabled);                       // optimistic
                          try {
                            const res = await employeeApi.setCorporate(employee.id, enabled);
                            setEmployee(res.data);                // authoritative (unit may have changed)
                            setCorp(!!res.data.is_corporate);
                            toast.success(res.data.is_corporate
                              ? 'Marked as corporate function — moved to Corporate Office'
                              : 'Removed corporate flag — unit restored');
                          } catch (err) {
                            setCorp(!!employee?.is_corporate);    // roll back on failure
                            toast.error(err?.response?.data?.detail || 'Failed to update');
                          }
                        }}
                      />
                      <span className="text-sm text-[#11182B]">
                        Corporate function — anyone in any unit can report to this person (moves them to Corporate Office)
                      </span>
                    </div>
                  )}
                </div>

                {/* Full education history. The legacy free-text Education field
                    above is left exactly as it is — this sits alongside it. */}
                <div className="mt-6">
                  <EducationSection employeeId={id} canEdit={canEditEmployee} onChanged={loadEmployee} />
                </div>

              </TabsContent>

              {canViewSalary && (
                <TabsContent value="salary" className="mt-6">
                  <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                    <div className="p-6 border-b border-[#E6E9F0] flex flex-wrap items-end justify-between gap-3">
                      <div>
                        <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Salary Progression</h2>
                        <p className="text-sm text-[#5A6377] mt-1">Year-by-year salary history since joining</p>
                      </div>
                      {canEditSalary && canViewSalary && (
                        <div className="flex items-end gap-2">
                          <div><label className="text-[10px] uppercase text-[#98A1B2] block">Year</label><Input type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)} className="w-24" /></div>
                          <div><label className="text-[10px] uppercase text-[#98A1B2] block">Gross (₹)</label><Input type="number" value={newGross} onChange={(e) => setNewGross(e.target.value)} className="w-32" /></div>
                          <Button data-testid="add-salary-year" size="sm" onClick={addSalaryYear}>Add / update year</Button>
                          <span className="text-[10px] text-[#98A1B2] self-center">Pre-filled with joining year &amp; joining salary — edit or click Add.</span>
                        </div>
                      )}
                    </div>
                    {salaryHistory.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs font-semibold tracking-wide uppercase border-b border-[#E6E9F0]">
                            <tr>
                              <th className="py-3 px-4 text-left">Year</th>
                              <th className="py-3 px-4 text-right">Gross Salary</th>
                              <th className="py-3 px-4 text-right">Increment</th>
                              <th className="py-3 px-4 text-right">Increment %</th>
                              <th className="py-3 px-4 text-left">Status</th>
                              {canEditSalary && <th className="py-3 px-4 text-left"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {salaryHistory.map(row => (
                              <tr key={row.id || row.year} data-testid={`salary-row-${row.year}`} className="border-b border-[#E6E9F0] last:border-0 hover:bg-[#F7F8FB]">
                                <td className="py-3 px-4 text-sm text-[#11182B] font-medium">{row.year}</td>
                                <td className="py-3 px-4 text-sm text-[#11182B] text-right">₹{Number(row.gross).toLocaleString()}</td>
                                <td className="py-3 px-4 text-sm text-[#15A36E] text-right">{row.increment > 0 ? `+₹${Number(row.increment).toLocaleString()}` : '-'}</td>
                                <td className="py-3 px-4 text-sm text-[#15A36E] text-right">{row.pct > 0 ? `${Number(row.pct).toFixed(2)}%` : '-'}</td>
                                <td className="py-3 px-4">{row.is_proposed && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FBF7EC] text-[#E9A23B]">Proposed</span>}</td>
                                {canEditSalary && <td className="py-3 px-4"><button data-testid={`del-salary-${row.year}`} onClick={() => deleteSalaryYear(row.year)} className="text-[#E11D2A] hover:underline text-xs">Remove</button></td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-12 text-center text-[#98A1B2]"><p>No salary history available or access restricted</p></div>
                    )}

                    {/* Under the progression table, in the same tab: one
                        HR-F-42 sheet per salary year above. The years come
                        from that table, so the two can never disagree about
                        which years exist. */}
                    <div className="px-6 pb-6">
                      <SalaryBreakupYears employeeId={id} canEdit={hasPerm('salary.edit')} />
                    </div>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="kpi" className="mt-6">
                <KpiTab employeeId={id} canEdit={canEditEmployee} />
              </TabsContent>

              <TabsContent value="suggestions" className="mt-6">
                <SuggestionsTab employeeId={id} canEdit={canEditEmployee} />
              </TabsContent>

              <TabsContent value="kaizen" className="mt-6">
                <KaizenTab employeeId={id} employee={employee} canEdit={canEditEmployee} />
              </TabsContent>

              <TabsContent value="letters" className="mt-6">
                <LettersTab employeeId={id} canEdit={hasPerm('letters.generate')} />
              </TabsContent>
              <TabsContent value="induction" className="mt-6">
                <InductionTab employeeId={id} canEdit={hasPerm('employee.edit')} />
              </TabsContent>
              <TabsContent value="documents" className="mt-6">
                <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Staff Joining Documents</h2>
                      <p className="text-sm text-[#5A6377]">{DOC_CHECKLIST.filter((it) => documents.some((d) => d.doc_type === it) || (it === 'Photo' && !!employee?.photo_url)).length} of {DOC_CHECKLIST.length} collected</p>
                    </div>
                  </div>

                  <div className="divide-y divide-[#F0F2F6]">
                    {DOC_CHECKLIST.map((item) => {
                      const doc = documents.find((d) => d.doc_type === item);
                      const fromPhoto = !doc && item === 'Photo' && !!employee?.photo_url;
                      const uploaded = !!doc || fromPhoto;
                      return (
                        <div key={item} className="flex items-center justify-between py-3">
                          <div className="flex items-center gap-3">
                            {uploaded ? <CheckCircle2 className="w-5 h-5 text-[#15A36E] flex-shrink-0" /> : <Circle className="w-5 h-5 text-[#CBD2E0] flex-shrink-0" />}
                            <div>
                              <p className="text-sm font-medium text-[#11182B]">{item}</p>
                              {doc ? <p className="text-[11px] text-[#98A1B2]">{doc.filename} · {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                : fromPhoto ? <p className="text-[11px] text-[#15A36E]">From profile photo</p>
                                : <p className="text-[11px] text-[#E9A23B]">Not uploaded</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc && (
                              <Button data-testid={`doc-view-${item}`} variant="ghost" size="sm"
                                      className="text-[#2563EB]"
                                      onClick={() => setViewDoc(doc)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {doc && <a href={documentApi.download(doc.id)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="text-[#2563EB]"><Download className="w-4 h-4" /></Button></a>}
                            {canUploadDocs && (
                              <label data-testid={`upload-${item}`} className="text-xs text-[#2563EB] cursor-pointer px-2 py-1 border border-[#E6E9F0] rounded-md hover:bg-[#EFF1F6]">
                                {doc ? 'Replace' : 'Upload'}
                                <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(item, f); e.target.value = ''; }} />
                              </label>
                            )}
                            {/* Scan: opens the DocumentScanner (live camera +
                                jscanify/OpenCV edge-detect & perspective-warp).
                                Feeds the SAME `uploadDoc` handler as gallery. */}
                            {canUploadDocs && (
                              <button type="button"
                                      data-testid={`doc-scan-${item}`}
                                      onClick={() => setScanFor(item)}
                                      className="text-xs text-[#2563EB] cursor-pointer px-2 py-1 border border-[#E6E9F0] rounded-md hover:bg-[#EFF1F6]">
                                Scan
                              </button>
                            )}
                            {doc && canUploadDocs && <button onClick={() => removeDoc(doc.id)} className="text-[#E11D2A] hover:underline text-xs">Delete</button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {documents.filter((d) => !DOC_CHECKLIST.includes(d.doc_type)).length > 0 && (
                    <div className="mt-6 pt-4 border-t border-[#E6E9F0]">
                      <h3 className="text-sm font-semibold text-[#11182B] mb-2">Other documents</h3>
                      <div className="space-y-2">
                        {documents.filter((d) => !DOC_CHECKLIST.includes(d.doc_type)).map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 border border-[#E6E9F0] rounded-lg">
                            <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-[#5A6377]" /><div><p className="text-sm font-medium text-[#11182B]">{doc.filename}</p><p className="text-[11px] text-[#98A1B2]">{doc.doc_type}</p></div></div>
                            <div className="flex items-center gap-2">
                              <Button data-testid={`doc-view-${doc.doc_type}`} variant="ghost" size="sm"
                                      className="text-[#2563EB]"
                                      onClick={() => setViewDoc(doc)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <a href={documentApi.download(doc.id)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="text-[#2563EB]"><Download className="w-4 h-4" /></Button></a>
                              {canUploadDocs && <button onClick={() => removeDoc(doc.id)} className="text-[#E11D2A] hover:underline text-xs">Delete</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {canUploadDocs && (
                    <div className="mt-5">
                      <label className="text-xs text-[#2563EB] cursor-pointer hover:underline inline-flex items-center gap-1">
                        <Upload className="w-3.5 h-3.5" /> Upload other document
                        <input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { const t = window.prompt('Document name?', 'Other'); if (t) uploadDoc(t, f); } e.target.value = ''; }} />
                      </label>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DocumentScanner
            open={!!scanFor}
            docName={scanFor || ''}
            onCancel={() => setScanFor(null)}
            onCapture={(file) => { const item = scanFor; setScanFor(null); if (item) uploadDoc(item, file); }}
          />

          <DocumentPreviewModal
            open={!!viewDoc}
            doc={viewDoc}
            fallbackName={viewDoc ? `${viewDoc.doc_type || 'document'}-${employee?.card_no || employee?.id || ''}` : ''}
            onClose={() => setViewDoc(null)}
          />

          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Edit {employee?.name}</DialogTitle></DialogHeader>
              <EmployeeEditForm employee={employee} masters={masters}
                onSaved={(updated, change) => {
                  setEmployee(updated); setEditOpen(false); toast.success('Saved');
                  if (!change) return;   // unrelated edit → leave any banners as-is
                  if (change.isPromotion || change.unitChanged || change.deptChanged) {
                    setPromoEffective(new Date().toISOString().slice(0, 10));
                  }
                  // Promotion banner
                  if (change.isPromotion && canIssueLetters) {
                    setPendingPromotion({ from: change.from, to: change.to });
                  } else if (change.desigChanged) {
                    setPendingPromotion(null);   // reverted / lateral → clear stale
                  }
                  // Transfer / reassignment banner
                  if ((change.unitChanged || change.deptChanged) && canIssueLetters) {
                    setPendingTransfer({
                      unitChanged: change.unitChanged, deptChanged: change.deptChanged,
                      fromUnit: change.fromUnit, toUnit: change.toUnit,
                      fromDept: change.fromDept, toDept: change.toDept,
                    });
                  } else {
                    setPendingTransfer(null);   // no unit/dept change in this save → clear stale
                  }
                  // Increment / salary-change banner
                  if (change.salaryChanged && canIssueLetters) {
                    setPendingIncrement({ from: change.fromSalary, to: change.toSalary });
                  } else {
                    setPendingIncrement(null);
                  }
                }} />
            </DialogContent>
          </Dialog>

          <ResignDialog employee={employee} open={resignOpen} onOpenChange={setResignOpen}
                        onDone={() => loadEmployee()} />

          <Dialog open={kpiLinkOpen} onOpenChange={setKpiLinkOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>QR code — {employee?.name}</DialogTitle></DialogHeader>
              <div className="text-center space-y-3">
                {kpiToken ? (
                  <>
                    <div className="text-[11px] font-medium text-[#15654B] bg-[#EAF7F0] border border-[#CDEBDB] rounded-md p-2">
                      This is {employee?.name}&apos;s <b>permanent</b> code. It never changes, so you can reprint it at any time.
                    </div>
                    <div className="flex justify-center"><QRCodeCanvas value={`${window.location.origin}/kpi/${kpiToken}`} size={180} includeMargin level="M" /></div>
                    <div data-testid="kpi-link-url" className="text-[11px] break-all bg-[#F7F8FB] border border-[#E6E9F0] rounded-md p-2 text-[#11182B]">{`${window.location.origin}/kpi/${kpiToken}`}</div>
                    <div className="flex justify-center gap-2">
                      <Button data-testid="kpi-link-copy-btn" variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/kpi/${kpiToken}`); toast.success('Link copied'); }}>Copy link</Button>
                      <Button data-testid="kpi-link-print-btn" variant="outline" size="sm" onClick={() => window.print()}>Print</Button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-[#5A6377]">Loading this employee&apos;s QR code…</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}

function EmployeeEditForm({ employee, masters, onSaved }) {
  const originalDesignation = employee?.designation || '';
  const originalSalary = employee?.joining_salary ?? null;
  const originalUnit = employee?.unit || '';
  const originalDepartment = employee?.department || '';
  // NO exit controls here, by design. Processing a departure is not an edit to
  // a field: it has its own dates, a mandatory reason, a vacancy and a notice
  // period behind it, and all of that lives in the Resign action beside this
  // button. A status dropdown in the middle of a details form was how an exit
  // got recorded with no reason and no last working day.
  const [f, setF] = useState({
    name: employee?.name || '', card_no: employee?.card_no || '', father_husband: employee?.father_husband || '',
    unit: employee?.unit || '', department: employee?.department || '', designation: employee?.designation || '',
    category: employee?.category || '', doj: (employee?.doj || '').includes('.') ? '' : (employee?.doj || ''),
    dob: (employee?.dob || '').includes('.') ? '' : (employee?.dob || ''), email: employee?.email || '', mobile: employee?.mobile || '',
    education: employee?.education || '', joining_salary: employee?.joining_salary ?? '',
    source_of_hiring: employee?.source_of_hiring || '', source_detail: employee?.source_detail || '',
    experience_type: employee?.experience_type || '',
    prior_experience_years: employee?.prior_experience_years ?? '',
  });
  const [saving, setSaving] = useState(false);
  const hiringMeta = useHiringMeta();
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const levelOf = (name) => {
    const d = (masters.designations || []).find((x) => x.name === name);
    return d && d.level != null ? d.level : null;
  };
  const save = async () => {
    setSaving(true);
    try {
      // Details only. `status` and the exit fields are never sent from here,
      // so saving an edit can neither start nor undo a departure.
      const payload = {
        ...f,
        joining_salary: f.joining_salary === '' ? null : parseFloat(f.joining_salary),
        // Blank means NOT RECORDED, which has to reach the server as null —
        // "" would be read as an invalid value rather than an absent one.
        source_of_hiring: f.source_of_hiring || null,
        source_detail: f.source_detail || null,
        experience_type: f.experience_type || null,
        prior_experience_years: f.experience_type
          ? (f.prior_experience_years === '' ? null : Number(f.prior_experience_years))
          : null,
      };
      const res = await employeeApi.update(employee.id, payload);
      // Promotion = designation changed and (level rose, or levels unknown so HR decides).
      const desigChanged = f.designation && f.designation !== originalDesignation;
      const oldLv = levelOf(originalDesignation);
      const newLv = levelOf(f.designation);
      const isPromotion = desigChanged && (oldLv == null || newLv == null ? true : newLv > oldLv);
      const unitChanged = !!(f.unit && f.unit !== originalUnit);
      const deptChanged = !!(f.department && f.department !== originalDepartment);
      const newSalaryNum = f.joining_salary === '' ? null : parseFloat(f.joining_salary);
      const salaryChanged = newSalaryNum != null && Number(originalSalary) !== newSalaryNum;
      const anyRelevant = desigChanged || unitChanged || deptChanged || salaryChanged;
      onSaved(res.data, anyRelevant ? {
        from: originalDesignation, to: f.designation, isPromotion, desigChanged,
        unitChanged, deptChanged,
        fromUnit: originalUnit, toUnit: f.unit,
        fromDept: originalDepartment, toDept: f.department,
        salaryChanged, fromSalary: originalSalary, toSalary: newSalaryNum,
      } : null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const sel = (val, onCh, items) => (
    <select className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" value={val} onChange={(e) => onCh(e.target.value)}>
      <option value="">— select —</option>{items.map((it) => <option key={it.id} value={it.name}>{it.name}</option>)}
    </select>);
  return (
    <div className="grid grid-cols-2 gap-4">
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Name</Label><Input data-testid="edit-name" value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Card No</Label><Input value={f.card_no} onChange={(e) => set('card_no', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Father/Husband</Label><Input value={f.father_husband} onChange={(e) => set('father_husband', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Unit</Label><div data-testid="edit-unit-wrap">{sel(f.unit, (v) => set('unit', v), masters.units)}</div></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Department</Label>{sel(f.department, (v) => set('department', v), masters.departments)}</div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Designation</Label>{sel(f.designation, (v) => set('designation', v), masters.designations)}</div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Date of Joining</Label><Input data-testid="edit-doj" type="date" value={f.doj} onChange={(e) => set('doj', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Date of Birth</Label><Input data-testid="edit-dob" type="date" value={f.dob} onChange={(e) => set('dob', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Email</Label><Input data-testid="edit-email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Contact Number</Label><Input data-testid="edit-mobile" type="tel" inputMode="numeric" maxLength={10} value={f.mobile} onChange={(e) => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Joining Salary</Label><Input type="number" value={f.joining_salary} onChange={(e) => set('joining_salary', e.target.value)} /></div>
      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Education</Label><Input value={f.education} onChange={(e) => set('education', e.target.value)} /></div>
      <SourceOfHiringFields meta={hiringMeta} value={f} idPrefix="edit"
                            onChange={(patch) => setF((s2) => ({ ...s2, ...patch }))} />
      <ExperienceFields meta={hiringMeta} value={f} idPrefix="edit"
                        onChange={(patch) => setF((s2) => ({ ...s2, ...patch }))} />
      <div>
        <Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Status</Label>
        <div data-testid="edit-status-readonly"
             className="border border-[#E6E9F0] bg-[#F7F8FB] rounded-md text-sm px-2 py-2 w-full text-[#5A6377]">
          {employee?.on_notice ? 'On Notice Period'
            : employee?.status === 'Left' ? (employee?.exit_reason || 'Left')
            : (employee?.status || 'Active')}
        </div>
        <p className="text-[11px] text-[#5A6377] mt-1">
          Set by the <b>Resign</b> action, not here — an exit needs its dates and a reason.
        </p>
      </div>
      <div className="col-span-2 flex justify-end"><Button data-testid="save-employee-edit" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button></div>
    </div>
  );
}
